import React, { FC, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { useSurveyTable } from '../../hooks';
import { ISurveyFilters } from '../../services/surveys';
import {
  ErrorMsg,
  ErrorMsgCont,
  ErrorMsgIcon,
  SearchBoxCont,
  SearchButton,
  SearchInput,
  SearchInputIcon,
  SearchInputCont,
} from './SearchBox.styles';
import SearchIcon from '@/assets/icons/searchIcon.svg?react';

interface ISearchBox {
  searchPhrase: (search: ISurveyFilters) => void;
  placeholder: string;
  isIcon?: boolean;
  filterName: string;
  isButtonAvailable?: boolean;
  minLength?: number;
}

const SearchBox: FC<ISearchBox> = ({
  searchPhrase,
  placeholder,
  isIcon = false,
  filterName,
  isButtonAvailable = 'true',
  minLength = 0,
}) => {
  const { t } = useTranslation();
  const { getAppliedFilters } = useSurveyTable();
  const filters = getAppliedFilters();
  const initialValue = filters[filterName] || null;

  // An empty field clears the filter, so only a non-empty phrase has to reach minLength.
  const searchSchema = useMemo(
    () =>
      object({
        [filterName]: string()
          .nullable()
          .transform(value => (value ? value.trim() : ''))
          .test(
            'searchMinLength',
            t('shared.search.minLength', { minLength }),
            value => !value || value.length >= minLength
          ),
      }),
    [filterName, minLength, t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    // ! TODO: have to be resolved later
    // @ts-expect-error task-21: @hookform/resolvers@5 is typed for yup 1, the app is on yup 0.32
    defaultValues: {
      [filterName]: initialValue,
    },
    resolver: yupResolver(searchSchema),
  });
  const searchError = errors[filterName];

  const doSearch = ({ [filterName]: search }: ISurveyFilters) => {
    if (!search) {
      return searchPhrase({ [filterName]: null });
    }
    return searchPhrase({ [filterName]: search.trim(), pageNo: null });
  };

  return (
    <form onSubmit={handleSubmit(doSearch)}>
      <SearchBoxCont>
        <SearchInputCont>
          <SearchInput
            data-testid="SearchBoxInput"
            {...register(filterName)}
            placeholder={placeholder}
            width="305px"
            padding={isIcon ? '12px 12px 12px 44px' : '12px'}
            error={Boolean(searchError)}
            aria-invalid={Boolean(searchError)}
          />
          {isIcon ? (
            <SearchInputIcon>
              <SearchIcon />
            </SearchInputIcon>
          ) : null}
          {searchError ? (
            <ErrorMsgCont data-testid="SearchBoxError">
              <ErrorMsgIcon />
              <ErrorMsg>{searchError.message as string}</ErrorMsg>
            </ErrorMsgCont>
          ) : null}
        </SearchInputCont>
        {isButtonAvailable && (
          <SearchButton data-testid="SearchBoxButton" type="submit">
            <SearchIcon />
            {t('surveys.table.metadata.search')}
          </SearchButton>
        )}
      </SearchBoxCont>
    </form>
  );
};

export default SearchBox;
